/**
 * ViasToolbarContent — Herramientas del módulo Vías
 *
 * @version 1.1
 * @updated 2026-04-12
 * @module core/modules/vias/components
 *
 * @description
 * Contenido de la sección modular del BottomToolbar para el módulo de
 * infraestructura vial. Se renderiza cuando el módulo Vías está activo.
 *
 * Renderiza herramientas reales del módulo:
 * Fila 1 — edición, inventario y actualización de métricas.
 * Fila 2 — herramientas analíticas activas:
 *   1. Flujo vial / tráfico (solo operar)
 *   2. Sintaxis espacial
 */

import {
  Route,
  GitMerge,
  TriangleAlert,
  Bike,
  AlertCircle,
  ClipboardList,
  Activity,
  Undo2,
  Redo2,
  Braces,
  Loader2,
  Check,
  X,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useOperationalModeStore } from '../../../operational-modes';
import {
  NETWORK_ANALYSIS_LABEL,
  useNetworkEdit,
  useNetworkAnalysisUiStore,
  useSyntaxAnalysisSafe,
  usePopulationSegmentSafe,
} from '../../../spatial';
import { canonicalNetworkApi } from '../../../spatial/services/canonical-network-api.service';
import { networkUndoRedoService } from '../../../spatial/services/network-undo-redo.service';
import { useProject } from '../../../projects';
import { useToast } from '../../../toast';
import type { ModuleToolbarProps } from '../../../module-registry';
import './vias-toolbar-content.css';

export function ViasToolbarContent({
  activeTool,
  onToolChange,
}: ModuleToolbarProps): JSX.Element {
  const { mode } = useOperationalModeStore();
  const { projectId } = useProject();
  const {
    undo,
    redo,
    canUndoNow,
    canRedoNow,
    hasPendingNetworkChanges,
    geometryEditEdgeId,
    cancelGeometryEdit,
    selectedEdgesForMerge,
    mergeValidationResult,
    mergeEdges,
    clearMergeSelection,
    // Multi-Merge V1 (Fase 3)
    selectedEdgeIds,
    chainValidation,
    confirmMultiMerge,
    clearSelection,
  } = useNetworkEdit();
  const { showToast } = useToast();

  // Syntax Analysis context (safe version - returns null if provider not mounted)
  const syntaxContext = useSyntaxAnalysisSafe();
  const syntaxStatus = syntaxContext?.status ?? 'idle';
  const { analysisActive, setAnalysisActive, activeIndicator, setActiveIndicator } = useNetworkAnalysisUiStore();

  // Population segment context (safe — null if provider not mounted)
  const populationContext = usePopulationSegmentSafe();
  const isCalculatingPopulation = populationContext?.isCalculating ?? false;

  // Commit state
  const [isCommitting, setIsCommitting] = useState(false);
  const [commitSuccess, setCommitSuccess] = useState(false);

  const isInventoryMode = mode === 'inventory';
  const isPlanMode      = mode === 'plan';
  const isAnalyzeMode   = mode === 'analyze';
  const isOperateMode   = mode === 'operate';
  const isCalculatingSyntax    = syntaxStatus === 'calculating';
  const isPopulationActive     = analysisActive && activeIndicator === 'population';
  const isSyntaxButtonActive   = analysisActive && activeIndicator !== 'population';

  // Modo de edición geométrica activo (sub-modo de inventario)
  const isGeometryEditMode = geometryEditEdgeId !== null;

  // Merge edges state (V1 2-edge merge)
  const canMergeEdges =
    selectedEdgesForMerge.length === 2 &&
    mergeValidationResult?.valid === true;

  // Multi-Merge V1 state (Fase 3)
  const canMultiMerge =
    selectedEdgeIds.length >= 2 &&
    chainValidation?.isValid === true;

  // DEBUG: Log para diagnosticar estado de undo/redo
  console.log('[ViasToolbarContent] 🔍 Render state:', {
    canUndoNow,
    canRedoNow,
    isInventoryMode,
    isGeometryEditMode,
    hasPendingNetworkChanges,
    undoDisabled: !canUndoNow, // Ahora solo depende de canUndoNow
  });

  const handleToolClick = (toolId: string): void => {
    const next = activeTool === toolId ? null : toolId;
    void onToolChange(next);
  };

  /**
   * Maneja click en el botón de análisis de red (sintaxis + estructura).
   * El indicador activo se conserva y la apertura del sidebar se coordina desde MapLayout.
   */
  const handleAnalysisClick = (): void => {
    if (isSyntaxButtonActive) {
      // Desactivar análisis si ya está activo en modo sintaxis
      setAnalysisActive(false);
    } else {
      // Activar análisis: si estaba en población, volver a integration_global
      if (activeIndicator === 'population') {
        setActiveIndicator('integration_global');
      }
      setAnalysisActive(true);
    }
  };

  /**
   * Activa/desactiva visualización de población por segmento.
   * Desactiva la sintaxis si estaba activa (solo una rampa de color a la vez).
   */
  const handlePopulationClick = (): void => {
    if (isPopulationActive) {
      // Segundo click desactiva
      setAnalysisActive(false);
    } else {
      setActiveIndicator('population');
      setAnalysisActive(true);
    }
  };

  /**
   * Finaliza el modo de edición geométrica
   * Ruta de salida equivalente a ESC
   */
  const handleFinalizarClick = (): void => {
    console.log('[GEOMETRY_EXIT] ════════════════════════════════════════════');
    console.log('[GEOMETRY_EXIT] 🟠 FINALIZAR EXIT PATH - Calling cancelGeometryEdit');
    console.log('[GEOMETRY_EXIT] ════════════════════════════════════════════');
    cancelGeometryEdit();
  };

  /**
   * Ejecuta merge de los 2 edges seleccionados (Fase 3)
   */
  const handleMergeEdges = async (): Promise<void> => {
    if (!canMergeEdges || selectedEdgesForMerge.length !== 2) return;

    try {
      const [edgeId1, edgeId2] = selectedEdgesForMerge;
      console.log('[ViasToolbarContent] 🔀 Merging edges:', { edgeId1, edgeId2 });

      await mergeEdges(edgeId1, edgeId2);

      // Limpiar selección después de merge exitoso
      clearMergeSelection();

      // Toast de éxito
      showToast('success', 'Segmentos fusionados correctamente', 3000);
    } catch (error: any) {
      console.error('[ViasToolbarContent] ❌ Error merging edges:', error);

      // Extraer mensaje específico del error del backend
      // La validación autoritativa ocurre en el backend y puede fallar por:
      // - Atributos incompatibles (roadClass, oneway, etc.)
      // - Referencias externas (asociaciones, rutas, etc.)
      // - Degree != 2 en nodo compartido (validación autoritativa)
      let errorMessage = 'Error desconocido al fusionar segmentos';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // Mostrar toast con error detallado
      // NOTA: No limpiamos la selección para que el usuario pueda revisar qué intentó fusionar
      showToast('error', `No se pudo fusionar: ${errorMessage}`, 7000);
    }
  };

  /**
   * Ejecuta multi-merge de N edges seleccionados (Multi-Merge V1 - Fase 3)
   */
  const handleMultiMerge = async (): Promise<void> => {
    if (!canMultiMerge || selectedEdgeIds.length < 2) return;

    try {
      const count = selectedEdgeIds.length;
      console.log('[ViasToolbarContent] 🔀 Multi-merging edges:', { count, edgeIds: selectedEdgeIds });

      await confirmMultiMerge();

      // Limpiar selección después de merge exitoso
      clearSelection();

      // Toast de éxito con número de segmentos
      showToast('success', `${count} segmentos fusionados correctamente`, 3000);
    } catch (error: any) {
      console.error('[ViasToolbarContent] ❌ Error multi-merging edges:', error);

      // Extraer mensaje específico del error del backend
      let errorMessage = 'Error desconocido al fusionar segmentos';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // Mostrar toast con error detallado
      // NOTA: No limpiamos la selección para que el usuario pueda revisar qué intentó fusionar
      showToast('error', `No se pudo fusionar: ${errorMessage}`, 7000);
    }
  };

  /**
   * Dispara recálculo de sintaxis espacial
   * NOTA: Los cambios de red YA están persistidos - esto solo actualiza métricas derivadas
   */
  const handleUpdateAnalysis = async (): Promise<void> => {
    if (!projectId || isCommitting) return;

    setIsCommitting(true);
    setCommitSuccess(false);

    try {
      const result = await canonicalNetworkApi.commitNetworkChanges(projectId);

      console.log('[ViasToolbarContent] ✅ Network changes committed:', result);

      // Limpiar historial de undo/redo
      networkUndoRedoService.clear();

      // Mostrar feedback de éxito
      setCommitSuccess(true);
      setTimeout(() => setCommitSuccess(false), 2000);

      // Toast de éxito
      showToast('success', 'Actualizando métricas de sintaxis espacial...', 4000);

    } catch (error) {
      console.error('[ViasToolbarContent] ❌ Error updating analysis:', error);

      // Toast de error
      const errorMessage = error instanceof Error ? error.message : 'Error al actualizar análisis';
      showToast('error', `Error: ${errorMessage}`, 5000);
    } finally {
      setIsCommitting(false);
    }
  };

  return (
    <>
      {/* ── Fila 1: Herramientas de edición e inventario ── */}
      <div className="toolbar-row">

        {/* Deshacer (Undo) - Habilitado siempre que haya algo que deshacer */}
        <button
          className="toolbar-square-btn"
          aria-label="Deshacer"
          title={!canUndoNow ? 'No hay acciones para deshacer' : 'Deshacer última acción (Ctrl+Z)'}
          onClick={() => undo()}
          disabled={!canUndoNow}
        >
          <Undo2 size={18} />
        </button>

        {/* Rehacer (Redo) - Habilitado siempre que haya algo que rehacer */}
        <button
          className="toolbar-square-btn"
          aria-label="Rehacer"
          title={!canRedoNow ? 'No hay acciones para rehacer' : 'Rehacer última acción (Ctrl+Y)'}
          onClick={() => redo()}
          disabled={!canRedoNow}
        >
          <Redo2 size={18} />
        </button>

        {/* ═══════════════════════════════════════════════════════════════════
            UNIR SEGMENTOS (V1 2-edge): Mostrar cuando hay 2 edges válidos seleccionados
            ═══════════════════════════════════════════════════════════════════ */}
        {canMergeEdges && (
          <button
            className="toolbar-commit-btn"
            aria-label="Unir segmentos"
            title={
              !isInventoryMode
                ? 'Debes estar en modo Inventariar'
                : 'Fusionar los 2 segmentos seleccionados en uno solo'
            }
            onClick={handleMergeEdges}
            disabled={!isInventoryMode}
          >
            <GitMerge size={16} />
            <span>Unir segmentos</span>
          </button>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            UNIR N SEGMENTOS (Multi-Merge V1): Mostrar cuando hay N ≥ 2 edges válidos
            (Fase 3 - Multi-Merge V1)
            ═══════════════════════════════════════════════════════════════════ */}
        {canMultiMerge && (
          <button
            className="toolbar-commit-btn"
            aria-label={`Unir ${selectedEdgeIds.length} segmentos`}
            title={
              !isInventoryMode
                ? 'Debes estar en modo Inventariar'
                : `Fusionar ${selectedEdgeIds.length} segmentos en cadena en uno solo`
            }
            onClick={handleMultiMerge}
            disabled={!isInventoryMode}
          >
            <GitMerge size={16} />
            <span>Unir {selectedEdgeIds.length} segmentos</span>
          </button>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            MODO EDICIÓN GEOMÉTRICA: Mostrar botón "Finalizar"
            (Los cambios se guardan automáticamente al soltar cada handle)
            ═══════════════════════════════════════════════════════════════════ */}
        {isGeometryEditMode && (
          <button
            className="toolbar-commit-btn toolbar-exit-btn"
            aria-label="Finalizar edición geométrica"
            title="Finalizar edición de geometría (ESC)"
            onClick={handleFinalizarClick}
          >
            <X size={16} />
            <span>Finalizar</span>
          </button>
        )}

        {/* Actualizar análisis - Dispara recálculo de sintaxis espacial
            NOTA: Los cambios de red YA están guardados, esto solo actualiza métricas */}
        {hasPendingNetworkChanges && !isGeometryEditMode && (
          <button
            className={`toolbar-commit-btn ${commitSuccess ? 'success' : ''}`}
            aria-label="Actualizar análisis"
            title={
              !isInventoryMode
                ? 'Debes estar en modo Inventariar'
                : isCommitting
                ? 'Actualizando métricas...'
                : 'Actualizar métricas de sintaxis espacial'
            }
            onClick={handleUpdateAnalysis}
            disabled={!isInventoryMode || isCommitting || !canUndoNow}
          >
            <Check size={16} />
            <span>{commitSuccess ? 'Actualizado' : isCommitting ? 'Actualizando...' : 'Actualizar'}</span>
          </button>
        )}

        {/* Agregar segmento de vía */}
        <button
          className={`toolbar-square-btn ${activeTool === 'add-segment' ? 'active' : ''}`}
          aria-label="Agregar segmento de vía"
          title={
            isGeometryEditMode
              ? 'Finaliza la edición geométrica primero'
              : !isInventoryMode
              ? 'Debes estar en modo Inventariar'
              : 'Trazar segmento de vía'
          }
          onClick={() => handleToolClick('add-segment')}
          disabled={!isInventoryMode || isGeometryEditMode}
        >
          <Route size={18} />
        </button>

        {/* Agregar intersección */}
        <button
          className={`toolbar-square-btn ${activeTool === 'add-intersection' ? 'active' : ''}`}
          aria-label="Agregar intersección"
          title={
            isGeometryEditMode
              ? 'Finaliza la edición geométrica primero'
              : !isInventoryMode
              ? 'Debes estar en modo Inventariar'
              : 'Registrar intersección'
          }
          onClick={() => handleToolClick('add-intersection')}
          disabled={!isInventoryMode || isGeometryEditMode}
        >
          <GitMerge size={18} />
        </button>

        {/* Agregar señal de tránsito */}
        <button
          className={`toolbar-square-btn ${activeTool === 'add-sign' ? 'active' : ''}`}
          aria-label="Agregar señal de tránsito"
          title={
            isGeometryEditMode
              ? 'Finaliza la edición geométrica primero'
              : !isInventoryMode
              ? 'Debes estar en modo Inventariar'
              : 'Registrar señal de tránsito'
          }
          onClick={() => handleToolClick('add-sign')}
          disabled={!isInventoryMode || isGeometryEditMode}
        >
          <TriangleAlert size={18} />
        </button>

        {/* Agregar ciclovía */}
        <button
          className={`toolbar-square-btn ${activeTool === 'add-bike-lane' ? 'active' : ''}`}
          aria-label="Agregar ciclovía"
          title={
            isGeometryEditMode
              ? 'Finaliza la edición geométrica primero'
              : !isInventoryMode
              ? 'Debes estar en modo Inventariar'
              : 'Trazar tramo de ciclovía'
          }
          onClick={() => handleToolClick('add-bike-lane')}
          disabled={!isInventoryMode || isGeometryEditMode}
        >
          <Bike size={18} />
        </button>

        {/* Registrar condición / daño */}
        <button
          className={`toolbar-square-btn ${activeTool === 'add-condition' ? 'active' : ''}`}
          aria-label="Registrar condición vial"
          title={
            isGeometryEditMode
              ? 'Finaliza la edición geométrica primero'
              : !isInventoryMode
              ? 'Debes estar en modo Inventariar'
              : 'Registrar bache, fisura u otro daño'
          }
          onClick={() => handleToolClick('add-condition')}
          disabled={!isInventoryMode || isGeometryEditMode}
        >
          <AlertCircle size={18} />
        </button>

        {/* Agregar proyecto vial (modo plan) */}
        <button
          className={`toolbar-square-btn ${activeTool === 'add-project' ? 'active' : ''}`}
          aria-label="Agregar proyecto vial"
          title={!isPlanMode ? 'Debes estar en modo Planificación' : 'Crear proyecto de intervención vial'}
          onClick={() => handleToolClick('add-project')}
          disabled={!isPlanMode}
        >
          <ClipboardList size={18} />
        </button>
      </div>

      {/* ── Fila 2: Herramientas de análisis ── */}
      <div className="toolbar-row">
        {isOperateMode && (
          <button
            className={`toolbar-square-btn ${activeTool === 'traffic-flow' ? 'active' : ''}`}
            aria-label="Flujo vial"
            title="Monitorear flujo y estado del tráfico"
            onClick={() => handleToolClick('traffic-flow')}
          >
            <Activity size={18} />
          </button>
        )}

        {/* Análisis de red urbana — Sintaxis espacial */}
        <button
          className={`toolbar-square-btn ${isSyntaxButtonActive ? 'active' : ''}`}
          aria-label={NETWORK_ANALYSIS_LABEL}
          title={
            !isAnalyzeMode
              ? 'Debes estar en modo Analizar'
              : isSyntaxButtonActive
              ? 'Ocultar análisis de sintaxis'
              : 'Abrir análisis de sintaxis'
          }
          onClick={handleAnalysisClick}
          disabled={!isAnalyzeMode}
        >
          {isCalculatingSyntax ? (
            <Loader2 size={18} className="toolbar-spinner" />
          ) : (
            <Braces size={18} />
          )}
        </button>

        {/* Población por segmento */}
        <button
          className={`toolbar-square-btn ${isPopulationActive ? 'active' : ''}`}
          aria-label="Población por segmento"
          title={
            !isAnalyzeMode
              ? 'Debes estar en modo Analizar'
              : isPopulationActive
              ? 'Ocultar población por segmento'
              : 'Mostrar población por segmento'
          }
          onClick={handlePopulationClick}
          disabled={!isAnalyzeMode}
        >
          {isCalculatingPopulation ? (
            <Loader2 size={18} className="toolbar-spinner" />
          ) : (
            <Users size={18} />
          )}
        </button>
      </div>
    </>
  );
}
